import { Component, effect } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import * as QuickSightEmbedding from 'amazon-quicksight-embedding-sdk';
import { DashboardService } from '../../../services/dashboard.service';
import { UtilitiesService } from '../../../services/utilities.service';
import { GlobalVariables } from '../../../constants/global-constants';
import { environment } from '../../../../environments/environment';
import { DASHBOARD } from '../../../../assets/i18n/dashboard-translations';
import { ClientAlertBannerComponent } from '../client-alert-banner/client-alert-banner.component';
import { MaintenanceBannerComponent } from '../maintenance-banner/maintenance-banner.component';


@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [MatProgressSpinner, ClientAlertBannerComponent, MaintenanceBannerComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardQuicksightComponent {
  embeddedDashboardExperience: QuickSightEmbedding.DashboardExperience;
  embedUrl: string;

  isLoading: boolean = true;
  isError: boolean = false;
  dashboardId: string;
  isDarkMode: boolean;
  parameterValue: number;
  isMaintenance: boolean = false;

  themeArn: string;
  clientName: string;

  language: string;
  translations: any;

  isDemo: boolean = false;
  isWooCommerceSession: boolean = false;

  constructor(
    private dashboardService: DashboardService,
    private utilitiesService: UtilitiesService,
  ) {
    effect(() => {
      this.isDarkMode = this.utilitiesService.isDarkTheme();
      this.themeArn = this.isDarkMode ? GlobalVariables.midnight : GlobalVariables.classic;
      this.applyFilter();
    });
  }

  ngOnInit() {
    this.isDemo = this.utilitiesService.isDemoValue;
    this.isWooCommerceSession = localStorage.getItem('wooCommerceSession') === 'true';
    this.isDarkMode = localStorage.getItem('theme') === GlobalVariables.darkTheme;
    this.themeArn = this.isDarkMode ? GlobalVariables.midnight : GlobalVariables.classic;
    this.clientName = this.utilitiesService.companyNameValue;
    this.language = localStorage.getItem('locale');
    this.translations = DASHBOARD[this.language] || DASHBOARD["en-us"];
    this.parameterValue = this.utilitiesService.clientId;
    this.getDashboardId();
    this.isMaintenance = environment.isMaintenance;
  }

  formatLocale(locale: string): string {
    if (locale) {
      return locale.toLowerCase().replace(/^([a-z])/, (match) => match.toUpperCase());
    }
    return 'en-EN'
  }

  getDashboardId() {
    this.dashboardService.getClientsToAccessDashboardSeller().subscribe({
      next: (res: {message: string, data: Array<{ fdClientId: number}>}) => {
        console.log(res);
        // if(res.data.some(client => client.fdClientId === this.utilitiesService.clientId)) {
        if (this.utilitiesService.clientId === 10655) {
          this.dashboardId = environment.dashboard_seller;
          this.getQuicksightEmbedURL(this.dashboardId);
        } else {
          this.dashboardId = environment.dashboard;
          this.getQuicksightEmbedURL(this.dashboardId);
        }
      },
      error: (err: any) => {
        console.log(err);
      }
    })
  }

  getQuicksightEmbedURL(dashboardId: string) {
    this.dashboardService.getQuicksightEmbedURL(dashboardId).subscribe({
      next: (res: any) => {
        if (res) {
          this.embedUrl = res.EmbedUrl;
          localStorage.removeItem('wooCommerceSession');
          this.embedVisual();
        }
      },
      error: (err: any) => {
        console.log(err);
      }
    })
  }

  getAnonymousEmbedURL(dashboardId: string) {
    const origin = window.location.origin;
    this.dashboardService.getAnonymousEmbedUrl(dashboardId, this.parameterValue, origin).subscribe({
      next: (res) => {
        if (res) {
          this.embedUrl = res.EmbedUrl;
          this.embedVisualAnonymous();
        }
      },
      error: (err: any) => {
        console.log(err);
      }
    });
  }

  //Quicksight Embedding
  async embedVisual() {
    const {
      createEmbeddingContext,
    } = QuickSightEmbedding;

    const embeddingContext = await createEmbeddingContext({
      onChange: (changeEvent, metadata) => {
        console.log('Context received a change', changeEvent, metadata);
      },
    });

    const frameOptions = {
      url: this.embedUrl,
      container: '#quicksight-dashboard',
      height: "100%",
      width: "100%"
    };

    const translationParams = Object.entries(this.translations).map(([key, value]) => ({
      Name: key,
      Values: [value]
    }));

    const lang = this.formatLocale(this.language)


    const contentOptions = {
      parameters: [
        {
          Name: 'fdClientID',
          Values: [
            this.parameterValue
          ],
        },
        {
          Name: 'isDemo',
          Values: this.isDemo
        },
        ...translationParams
      ],
      locale: lang,
      themeOptions: {
        themeArn: this.themeArn
      },
      onMessage: async (messageEvent) => {
        switch (messageEvent.eventName) {
          case 'CONTENT_LOADED': {
            this.isLoading = false;
            break;
          }
          case 'ERROR_OCCURRED': {
            this.isError = true;
            this.isLoading = false;
            break;
          }
        }
      },
    };


    //@ts-ignore
    this.embeddedDashboardExperience = await embeddingContext.embedDashboard(frameOptions, contentOptions);
  };


  async embedVisualAnonymous() {
    const { createEmbeddingContext } = QuickSightEmbedding;

    const embeddingContext = await createEmbeddingContext({
      onChange: (changeEvent, metadata) => {
        console.log('Context received a change', changeEvent, metadata);
      },
    });

    const frameOptions = {
      url: this.embedUrl,
      container: '#quicksight-dashboard',
      height: '100%',
      width: '100%',
    };

    const contentOptions = {
      locale: this.formatLocale(this.language),
      themeOptions: {
        themeArn: this.themeArn,
      },
      onMessage: async (messageEvent) => {
        switch (messageEvent.eventName) {
          case 'CONTENT_LOADED': {
            this.isLoading = false;
            break;
          }
          case 'ERROR_OCCURRED': {
            this.isError = true;
            this.isLoading = false;
            break;
          }
        }
      },
    };

    //@ts-ignore
    this.embeddedDashboardExperience = await embeddingContext.embedDashboard(frameOptions, contentOptions);
  }

  applyFilter() {
    this.embeddedDashboardExperience?.setTheme(this.themeArn);
  }
}

